import { useMemo, useEffect, useCallback, useState, useRef } from "react"
import { DrilldownSeedContext } from "connected-components/dashboard/drilldown/drilldown-seed-context"
import PropTypes from "prop-types"
import { isEqual, keys, chain, size, compact } from "lodash"
import moment from "moment"
import { Box } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import Error from "components/error"
import Loading from "components/loading"
import { useSyncAssetViewPreferences } from "connected-components/dashboard/hooks/use-sync-asset-view-preferences"
import { ERROR, OK } from "helpers/data-statuses"
import { useMetricSelectorMetrics } from "helpers/metrics/hooks"
import { flattenMetrics, getCustomSelectedMetrics } from "helpers/metrics"
import { usePositionSetBetaInstrument, useColumnOrderOptions } from "connected-components/asset-view/helpers"
import { beginDuplicateBuiltInView } from "connected-components/asset-view/helpers/duplicate-view-flow"
import { useDuplicateFlow } from "connected-components/asset-view/hooks/use-duplicate-flow"
import { useInstrumentsWithQueryOptions, useAvailableGroupByOptions, getOrderedInstrumentColumns } from "connected-components/dashboard/helpers"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useModelId } from "connected-components/dashboard/hooks/use-model-id"
import { combineTanStackStatuses, DATA_STATUSES } from "helpers/data-statuses"
import { hasIdioWeightingRule, isComparisonInstrumentOriginal } from "gatherers/helpers"
import { useHasUnexpandedComposites } from "resources/portfolios"
import { useModelMetadata } from "resources/models"
import { useTemplates } from "resources/templates"
import { useTemplateMetadata } from "resources/templates"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { LONG_SHORT_GROUPING, OVERWEIGHT_UNDERWEIGHT_GROUPING } from "helpers/rollups"
import { useClassifications } from "resources/classifications"
import { useResourcesList } from "resources/resource"
import { useAnalytics } from "connected-components/analytics"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { ASSET_VIEW_EVENTS } from "./analytics/events"
import { AssetViewAnalyticsProvider } from "./analytics/provider"
import AssetViewToolbar from "./components/asset-view-toolbar"
import DuplicateViewButton from "./components/duplicate-view-button"
import { useDisplayedMetricIds } from "./hooks/use-displayed-metric-ids"
import { useCompositionVisibility } from "./hooks/use-source-displayed-metric-ids"
import summaryTemplate from "./templates/summary"
import exposureTemplate from "./templates/exposure"
import riskTemplate from "./templates/risk"
import performanceTemplate from "./templates/performance"
import betaTemplate from "./templates/beta"
import customTemplate from "./templates/custom"
import contentSetTemplate from "./templates/content-set"
import marginalContributionTemplate from "./templates/marginal-contribution"
import factorSensitivityTemplate from "./templates/factor-sensitivity"
import { ColumnOrderProvider } from "./context/column-order"
import { DatagridPersistenceContext } from "renderers/contributors-datagrid/hooks/use-datagrid-persistence/datagrid-persistence-context"
import { COLUMN_ORDER_OPTIONS_ID, useHighlightedTradeRowIds } from "./helpers"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { OP_VIEW_LABELS } from "connected-components/dashboard/components/view-selectors/assets-menu-items"

import "./style.scss"

function makeViewTemplates(contentSets) {
  const contentSetTemplates = chain(contentSets)
    .keyBy("id")
    .mapValues(() => contentSetTemplate)
    .value()

  return {
    summary: summaryTemplate,
    exposure: exposureTemplate,
    performance: performanceTemplate,
    risk: riskTemplate,
    beta: betaTemplate,
    marginalContribution: marginalContributionTemplate,
    predictedFactorSensitivity: factorSensitivityTemplate,
    historicalFactorSensitivity: factorSensitivityTemplate,
    ...contentSetTemplates,
  }
}

export default function AssetViewWrapper(props) {
  const {
    contentSets,
    initialView,
    duplicateFromPanelId,
    shouldUseLegacyPreferences,
    syncDashboardModuleConfig,
    drilldownSeed,
  } = props
  // Seed initialView before the sync: calling useAssetViewData first registers its seed effect ahead
  // of the legacy fallback load (which omits "view"). Mirrors KPIWrapper.
  const assetViewData = useAssetViewData(initialView)
  const isAssetViewPreferencesLoaded = useSyncAssetViewPreferences({
    shouldUseLegacyPreferences,
    initialView,
    duplicateFromPanelId,
  })
  const { trades, secondInstrument: rebalanced } = assetViewData
  const modelId = useModelId()
  const highlightedRowIds = useHighlightedTradeRowIds(trades, rebalanced)

  const { view, setView, isPreferencesLoaded } = assetViewData

  useEffect(() => {
    if (!isPreferencesLoaded || !syncDashboardModuleConfig || !shouldUseLegacyPreferences) {
      return
    }
    if (view == null) {
      return
    }
    syncDashboardModuleConfig({ initialView: view })
  }, [isPreferencesLoaded, syncDashboardModuleConfig, shouldUseLegacyPreferences, view])
  const [templates, templatesStatus] = useTemplates()
  const viewTemplates = useMemo(() => makeViewTemplates(contentSets), [contentSets])
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)

  // If the selected view is not a standard template and not an existing custom template, set it to default custom
  // This is to prevent the user from seeing a template that doesn't exist
  // We want to do this in the AssetViewWrapper to avoid multiple renders becasue we change the view
  useEffect(() => {
    const isNotStandardTemplate = !keys(viewTemplates).includes(view)
    const isNotExistingCustomTemplate = !templates?.find(template => template.id === view)
    if (templatesStatus === OK && isNotStandardTemplate && isNotExistingCustomTemplate) {
      setView("custom")
    }
  }, [templates, view, setView, templatesStatus, viewTemplates])

  const status = combineTanStackStatuses([templatesStatus, modelMetadataStatus])

  if (status === ERROR) {
    return <Error />
  }

  if (!isAssetViewPreferencesLoaded) {
    return <Loading />
  }

  return (
    <DrilldownSeedContext.Provider value={drilldownSeed ?? null}>
      <AssetViewAnalyticsProvider>
        <AssetView
          {...{
            ...props,
            templates,
            templatesStatus,
            highlightedRowIds,
            viewTemplates,
            modelMetadata,
          }}
        />
      </AssetViewAnalyticsProvider>
    </DrilldownSeedContext.Provider>
  )
}

AssetViewWrapper.propTypes = {
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  initialView: PropTypes.string,
  duplicateFromPanelId: PropTypes.string,
  isSecondary: PropTypes.bool,
  drilldownSeed: PropTypes.shape({
    view: PropTypes.string.isRequired,
    selectedCategory: PropTypes.string,
    sourcePanelId: PropTypes.string.isRequired,
  }),
  shouldUseLegacyPreferences: PropTypes.bool,
  syncDashboardModuleConfig: PropTypes.func,
}

function AssetView(props) {
  const { highlightedRowIds, instrumentsWithMetadata, templates, templatesStatus, contentSets, viewTemplates, modelMetadata } = props
  const assetViewContext = useAssetViewData()
  const { modelId, endDate, view, groupBy, setGroupBy, showNotHeld, setShowNotHeld, instrument: original, secondInstrument: rebalanced, comparisonInstrument: comparison, base, filter, normalization, selectedMode, isExpanded, setExpanded, actionsBase, columnOrder, setColumnOrder, isEndVsStartDateComparison, setAssetViewGroupBy, datagridState, setDatagridState } = assetViewContext

  // Provide this panel's asset-view datagrid state to the shared ContributorsDataGrid persistence.
  const datagridPersistenceSource = useMemo(() => ({ datagridState, setDatagridState }), [datagridState, setDatagridState])

  const [selectedMetrics, setSelectedMetrics] = useState(null)
  const hasWarnedExpansionRef = useRef(false)
  const { sourcedMetricIdsRef, handleDisplayedMetricIdsChange } = useDisplayedMetricIds(view)

  const duplicateFlow = useDuplicateFlow()

  const isExpansionDisabled = useMemo(() => {
    return Boolean(hasIdioWeightingRule(comparison))
  }, [comparison])

  const analytics = useAnalytics()
  const dispatch = useDispatch()

  // [TODO] [APP-1129] make it work for non-portfolios
  const portfolioId = original?.type === "PORTFOLIO" && original?.id
  const [hasUnexpandedComposites] = useHasUnexpandedComposites(modelId, portfolioId, moment(endDate))
  const hasMultipleInstruments = useMemo(() => size(compact([original, rebalanced, comparison])) > 1, [original, comparison, rebalanced])
  const hasMultipleUniqueInstruments = hasMultipleInstruments && !isComparisonInstrumentOriginal(original, comparison)
  const [availableGroupByOptions, availableGroupByOptionsStatus] = useAvailableGroupByOptions(original, endDate, { isActive: Boolean(base), hasMultipleUniqueInstruments })
  const templateId = view === "custom" || viewTemplates[view] ? null : view
  const [selectedTemplateMetadata, selectedTemplateMetadataStatus] = useTemplateMetadata(templateId)
  const selectedContentSetMetadata = useMemo(() => contentSets.find(contentSet => contentSet.id === view), [contentSets, view])
  const [classifications, classificationsStatus] = useClassifications()
  const [forecasts, forecastsStatus] = useResourcesList("forecast")
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)

  const instrumentOptions = { isExpanded, normalization, filter }
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison }, selectedMode, instrumentOptions)
  const instrumentColumns = useMemo(() => getOrderedInstrumentColumns(selectedMode), [selectedMode])
  const isActive = Boolean(base)
  const compositionVisibility = useCompositionVisibility(instrumentColumns, original)

  const betaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata.original, modelMetadata)
  const [availableMetrics, availableMetricsStatus] = useMetricSelectorMetrics(modelMetadata?.id, instrumentsWithMetadata.original, betaInstrument, endDate)
  const flattenedMetrics = useMemo(() => flattenMetrics(availableMetrics), [availableMetrics])
  const metricsForTable = useMemo(() => {
    if (!selectedMetrics || selectedMetrics.length === 0) {
      return []
    }
    if (isIntradayAnalyticsEnabled) {
      return selectedMetrics
    }
    return selectedMetrics.filter((id) => !String(id).startsWith("intraday."))
  }, [selectedMetrics, isIntradayAnalyticsEnabled])

  const customSelectedMetrics = useMemo(() => getCustomSelectedMetrics(
    metricsForTable,
    flattenedMetrics,
    modelMetadata,
    instrumentsWithMetadata,
    forecasts,
    classifications,
    isEndVsStartDateComparison,
    contentSets,
  ), [metricsForTable, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, classifications, isEndVsStartDateComparison, contentSets])

  const isCustomTemplate = view === "custom" || !viewTemplates[view]
  const selectedView = isCustomTemplate ? "custom" : view
  const { options: columnOrderOptions, order } = useColumnOrderOptions(selectedView, selectedMode)

  const hasPerformanceMetrics = useMemo(() => {
    return isCustomTemplate && customSelectedMetrics?.some(metric => metric.prefix === "performance")
  }, [isCustomTemplate, customSelectedMetrics])

  const handleResetLongShortGroupBy = useCallback(() => {
    if (isActive && groupBy.id === "LONG_SHORT") {
      setGroupBy(OVERWEIGHT_UNDERWEIGHT_GROUPING)
    }
    if (!isActive && groupBy.id === "OVERWEIGHT_UNDERWEIGHT") {
      setGroupBy(LONG_SHORT_GROUPING)
    }
  }, [groupBy, setGroupBy, isActive])

  const handleResetAttributeTagGroupBy = useCallback(() => {
    if (!isActive && !hasMultipleUniqueInstruments) {
      return
    }
    const isAttributeOrTag = groupBy.isTag || groupBy.isPositionAttribute
    if (isAttributeOrTag) {
      setGroupBy(null)
    }
  }, [groupBy, setGroupBy, isActive, hasMultipleUniqueInstruments])

  // Analytics-enhanced callbacks
  const handleGroupingChange = useCallback((newGrouping) => {
    if (!newGrouping) {
      analytics.track(ASSET_VIEW_EVENTS.GROUPING_CLEARED, { groupBy: null, groupByType: null })
    } else {
      analytics.track(ASSET_VIEW_EVENTS.GROUPING_CHANGED, {
        groupBy: newGrouping?.id,
        groupByType: newGrouping?.type,
      })
    }
    setGroupBy(newGrouping)
  }, [analytics, setGroupBy])

  const handleExpansionToggle = useCallback((newExpanded) => {
    analytics.track(ASSET_VIEW_EVENTS.EXPANSION_TOGGLED, { isExpanded: newExpanded })
    setExpanded(newExpanded)
  }, [analytics, setExpanded])

  const handleDuplicateOPView = useCallback(() => {
    const contentSet = contentSets?.find((contentSetItem) => contentSetItem.id === view)
    const viewLabel = OP_VIEW_LABELS[view] ?? contentSet?.name ?? view
    const sourced = sourcedMetricIdsRef.current
    beginDuplicateBuiltInView({
      viewName: `${viewLabel} - duplicate`,
      sourceViewId: view,
      sourcedMetricIds: sourced?.view === view ? sourced.metricIds : null,
      duplicateFlow,
    })
  }, [view, contentSets, duplicateFlow, sourcedMetricIdsRef])

  useEffect(() => {
    setAssetViewGroupBy(groupBy)
  }, [groupBy, setAssetViewGroupBy])

  // Validate that the saved groupBy preference is available in current options
  useEffect(() => {
    if (groupBy && availableGroupByOptions && availableGroupByOptions.length > 0) {
      const isGroupByAvailable = availableGroupByOptions.some(rollup =>
        rollup.options
          ? rollup.options.some(option => isEqual(option, groupBy))
          : isEqual(rollup, groupBy),
      )
      if (!isGroupByAvailable) {
        setGroupBy(null)
      }
    }
  }, [groupBy, availableGroupByOptions, setGroupBy])

  useEffect(() => {
    if (groupBy) {
      handleResetLongShortGroupBy()
      handleResetAttributeTagGroupBy()
    }
  }, [groupBy, handleResetLongShortGroupBy, handleResetAttributeTagGroupBy])

  useEffect(() => {
    if ([COLUMN_ORDER_OPTIONS_ID.DEFAULT, COLUMN_ORDER_OPTIONS_ID.CUSTOM].includes(columnOrder)) {
      return
    }
    if (order) {
      setColumnOrder(order)
    }
  }, [columnOrder, setColumnOrder, order])

  useEffect(() => {
    if (!isExpansionDisabled) {
      hasWarnedExpansionRef.current = false
    }

    if (isExpansionDisabled && isExpanded && !hasWarnedExpansionRef.current) {
      hasWarnedExpansionRef.current = true
      setExpanded(false)
      dispatch(NotificationCreators.add("warning", null, "Expanded view is not supported when idio weighting rules are applied."))
    }
  }, [isExpansionDisabled, isExpanded, setExpanded, dispatch])

  const status = combineTanStackStatuses([selectedTemplateMetadataStatus, availableGroupByOptionsStatus, availableMetricsStatus, classificationsStatus, forecastsStatus])

  if (status === ERROR) {
    return <Error />
  }

  let template = viewTemplates[view]
  if (!template) {
    template = customTemplate
  }

  const headerContent = template.Header && <template.Header {...{ templateMetadata: selectedTemplateMetadata, modelMetadata, selectedContentSetMetadata, customSelectedMetrics, flattenedMetrics, onMetricsChange: handleDisplayedMetricIdsChange, compositionVisibility }} />

  const disableActiveTrading = base && actionsBase && !isEqual(base, actionsBase)

  const isTemplatesLoading = templatesStatus !== OK
  const isGroupingLoading = availableGroupByOptionsStatus !== OK
  const isMetricsLoading = availableMetricsStatus !== OK
  const isDuplicateViewDisabled = isTemplatesLoading || isMetricsLoading

  const duplicateViewButton = !isCustomTemplate ? (
    <DuplicateViewButton
      onClick={handleDuplicateOPView}
      disabled={isDuplicateViewDisabled}
    />
  ) : null

  const toolbarProps = {
    templates,
    contentSets,
    isCustomTemplate,
    headerContent,
    duplicateViewButton,
    columnOrder,
    setColumnOrder,
    columnOrderOptions,
    base,
    showNotHeld,
    setShowNotHeld,
    groupBy,
    handleGroupingChange,
    availableGroupByOptions,
    hasMultipleInstruments,
    isExpanded,
    handleExpansionToggle,
    hasUnexpandedComposites,
    isTemplatesLoading,
    isGroupingLoading,
    hasPerformanceMetrics,
    isExpansionDisabled,
  }

  const content = <Box className="asset-view">
    <AssetViewToolbar {...toolbarProps} />
    <ColumnOrderProvider columnOrder={columnOrder} onColumnOrderChange={setColumnOrder}>
      <template.Component
        modelMetadata={modelMetadata}
        templateMetadata={selectedTemplateMetadata}
        instrumentsWithMetadata={instrumentsWithMetadata}
        instrumentsWithQueryOptions={instrumentsWithQueryOptions}
        highlightedRowIds={highlightedRowIds}
        disableActiveTrading={disableActiveTrading}
        instrumentColumns={instrumentColumns}
        availableMetrics={availableMetrics}
        flattenedMetrics={flattenedMetrics}
        classifications={classifications}
        betaInstrument={betaInstrument}
        forecasts={forecasts}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        customSelectedMetrics={customSelectedMetrics}
        onMetricsChange={handleDisplayedMetricIdsChange}
        contentSets={contentSets}
        {...props}
      />
    </ColumnOrderProvider>
  </Box>

  return (
    <DatagridPersistenceContext.Provider value={datagridPersistenceSource}>
      {content}
    </DatagridPersistenceContext.Provider>
  )
}

AssetView.propTypes = {
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object),
  templatesStatus: PropTypes.oneOf(DATA_STATUSES),
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  viewTemplates: PropTypes.object.isRequired,
  modelMetadata: PropTypes.object.isRequired,
}
